import React, { useState, useContext } from 'react';

import { navigate } from '@reach/router';

import { IAdminGroup } from '../../../../store/admin/groups/store';
import { IAdminMembership } from '../../../../store/admin/memberships/store';

import List from '../../../../components/Lists/List/List';
import { PX } from '../../../../contrib/ui';
import { Player } from './Player';
import { PlayerListTitleRow } from './PlayerListTitleRow';
import { getCustomSorting } from './getCustomSorting';
import { GlobalContext } from '../../../../store';
import { useBoolean } from '../../../common/Hooks/useBoolean.hook';

export type TOrderType = 'ascending' | 'descending' | 'original';

interface IPlayeristProps {
  invited?: number[];
  eventId?: number;
  members: IAdminMembership[];
  memberSelection: (member: IAdminMembership) => void;
  search?: string;
  selectedMembers: IAdminMembership[];
  groups?: IAdminGroup[];
  groupEdition?: boolean;
  mobileVersion?: boolean;
}

const Playerist: React.FunctionComponent<IPlayeristProps> = props => {
  const {
    members,
    selectedMembers,
    memberSelection,
    invited,
    groupEdition,
    groups,
    search,
    mobileVersion
  } = props;

  const {
    actions: {
      adminMemberships: { updateByUser },
      adminGroups: { getGroups }
    }
  } = useContext(GlobalContext);

  const [customSorting, setCustomSorting] = useState<any>();
  const [order, setOrder] = useState<TOrderType>();
  const [type, setType] = useState<string>();
  const [tagMenuIsOpen, , toggleMenu] = useBoolean(false);
  const [currentTagMenuId, setCurrentTagMenuId] = useState(0);
  const [anchorEl, setAnchorEl] = useState<any>(null);

  const sort = (sortBy: string) => (_: any) => {
    let newOrder: TOrderType = 'ascending';
    const parts = sortBy.split('.');
    const orders = {
      ascending: 'descending' as TOrderType,
      descending: 'original' as TOrderType,
      original: 'ascending' as TOrderType
    };

    if (type === sortBy && order) {
      newOrder = orders[order];
    }

    setCustomSorting(getCustomSorting(parts, newOrder));
    setType(sortBy);
    setOrder(newOrder);
  };

  const filter = (membership: IAdminMembership): boolean => {
    if (!search) {
      return true;
    }

    const {
      user: { firstName, lastName, email }
    } = membership;

    const name = `${firstName} ${lastName} ${email}`;

    return name.toUpperCase().includes(search.toUpperCase());
  };

  const handleClose = (event: any) => {
    if (anchorEl.current && anchorEl.current.contains(event.target as HTMLElement)) {
      return;
    }

    setAnchorEl(null);
    setCurrentTagMenuId(0);
    toggleMenu();
  };

  const handleMenu = (playerId: number) => (event: any) => {
    setAnchorEl(event.currentTarget);
    setCurrentTagMenuId(playerId);
    toggleMenu();
  };

  const onChangeTag = (
    playerGroups: any,
    checkedGroupId: number,
    player: IAdminMembership
  ) => async (event: any) => {
    const newGroups = event.target.checked
      ? playerGroups.concat([checkedGroupId])
      : playerGroups.filter((id: number) => id !== checkedGroupId);

    const data = { groups: newGroups };

    await updateByUser(player.club, player.id, data);

    await getGroups(player.club, true);
  };

  const goToPlayerProfile = (clubId: number, playerId: number) => () => {
    navigate(`/staff/club/${clubId}/player-info/${playerId}`);
  };

  return (
    <div className="admin-form-radios column addons">
      <PlayerListTitleRow
        order={order}
        type={type}
        sort={sort}
        groupEdition={groupEdition}
        mobileVersion={mobileVersion}
      />

      <div className="list-scroll">
        <List
          className="list-no-padding"
          items={members}
          renderItem={Player}
          itemHeight={48 * PX}
          context={{
            anchorEl,
            currentTagMenuId,
            goToPlayerProfile,
            groupEdition,
            groups,
            handleClose,
            handleMenu,
            invited,
            memberSelection,
            mobileVersion,
            onChangeTag,
            selectedMembers,
            tagMenuIsOpen
          }}
          customSort={customSorting}
          customFilter={filter}
        />
      </div>
    </div>
  );
};

export default Playerist;
